/* eslint-disable */
// ═══════════════════════════════════════════════════════════════════════════
// CatalogPill — pill colorée réutilisable pour afficher un élément du catalogue
// Utilisé partout où on doit afficher un type, statut, sous-type, cabinet...
// ═══════════════════════════════════════════════════════════════════════════
import React from 'react';

/**
 * Calcule la luminance d'une couleur hex (0 = noir, 1 = blanc).
 */
function luminance(hexColor) {
  if (!hexColor || typeof hexColor !== 'string') return 0.5;
  let hex = hexColor.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
  if (hex.length !== 6) return 0.5;
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/**
 * Couleur de texte adaptée au fond.
 * - Fond clair → texte sombre #1a1a1a
 * - Fond sombre → texte clair #ffffff
 * - Pas de fond → gris neutre qui marche light+dark
 */
function getTextColor(hexColor) {
  if (!hexColor || typeof hexColor !== 'string') return '#9ca3af';
  const lum = luminance(hexColor);
  return lum > 0.65 ? '#1a1a1a' : '#ffffff';
}

/**
 * Couleur de fond — fallback gris neutre qui marche dans les 2 thèmes.
 */
function getBgColor(hexColor) {
  if (!hexColor || typeof hexColor !== 'string') return 'rgba(128,128,128,0.18)';
  return hexColor;
}

export default function CatalogPill({
  label,
  emoji,
  color,
  size = 'sm',
  onClick,
  removable = false,
  onRemove,
  style: extraStyle = {},
}) {
  if (!label) return null;
  const sizeStyles = {
    xs: { fontSize: '10px', padding: '2px 7px' },
    sm: { fontSize: '11px', padding: '3px 9px' },
    md: { fontSize: '12px', padding: '4px 11px' },
  };
  return (
    <span
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        borderRadius: '999px',
        background: getBgColor(color),
        color: getTextColor(color),
        fontWeight: 500,
        whiteSpace: 'nowrap',
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        ...sizeStyles[size],
        ...extraStyle,
      }}
    >
      {emoji && <span>{emoji}</span>}
      <span>{label}</span>
      {removable && (
        <span
          onClick={(e) => { e.stopPropagation(); onRemove && onRemove(); }}
          style={{ marginLeft: '2px', opacity: 0.7, cursor: 'pointer', fontSize: '0.85em' }}
        >×</span>
      )}
    </span>
  );
}

export function CatalogPillsStack({ items = [], size = 'sm', onClick, max }) {
  if (!items || items.length === 0) {
    return <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>—</span>;
  }
  const visible = max ? items.slice(0, max) : items;
  const remaining = max && items.length > max ? items.length - max : 0;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center' }} onClick={onClick}>
      {visible.map((it, i) => (
        <CatalogPill key={it.id || i} label={it.name} color={it.color} emoji={it.emoji} size={size} />
      ))}
      {remaining > 0 && (
        <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>+{remaining}</span>
      )}
    </div>
  );
}
